import { Box } from '@mui/system';
import { useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  getAllRolesQuery,
  UserKeys,
  addRoleMembers,
} from '../../../../../api/users-api';
import { RoleMember } from '../../../../../../types';
import { getRoleMembers } from '../../../../../api/users-api';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import AddRoleMemberDialog from './add-member-dialog';
import DeleteRoleMembersDialog from './delete-members-dialog';
import { useParams } from 'react-router-dom';
import { useUserSettings } from '../../../../../utils/use-user-settings';
import { Messages } from '../../../../../utils/message-constants';
import { createGridOverlays } from '../../../../grid-overlay/no-data-overlay-helpers';
import RoleMembershipExpirationChip from './role-membership-expiration-chip';
import { useStore } from '../../../../state/zustand-store';
import SharedDataGridClient from '../../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../../shared/shared-toolbar-latest';

import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Delete from '@mui/icons-material/Delete';
import Group from '@mui/icons-material/Group';
import Refresh from '@mui/icons-material/Refresh';

export default function RoleMembership() {
  const { roleName } = useParams() as { roleName: string };
  const { changeLogRoles, administrationRoles, adminEmail } = useUserSettings();
  const { setSnackbar } = useStore();
  const { data: roles, isLoading } = useQuery(getAllRolesQuery());
  const currentRole = roles?.find((role) => role.roleName === roleName);

  const queryClient = useQueryClient();
  const [openAddMembers, setOpenAddMembers] = useState(false);
  const [openDeleteMembersFromRole, setOpenDeleteMembersFromRole] =
    useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [updatingMemberEmails, setUpdatingMemberEmails] = useState<string[]>(
    []
  );

  const {
    data,
    isLoading: membersLoading,
    isFetching,
  } = useQuery({
    queryKey: [UserKeys.RoleMembers, roleName],
    queryFn: () => getRoleMembers(roleName),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('role members'),
    },
  });

  const { mutate: updateMemberMutate } = useMutation({
    mutationFn: (members: RoleMember[]) =>
      addRoleMembers(
        members.map(
          ({ userEmail, role, isAppAdmin, expiryTime, memberType }) => ({
            userEmail,
            role,
            isAppAdmin,
            expiryTime,
            ...(memberType && { memberType }),
          })
        ),
        ''
      ),
    onSuccess: (returnVal, variables) => {
      if (returnVal.status === 'SUCCESS') {
        const isDeleting = variables.every((v) => !v.expiryTime);
        setSnackbar({
          open: true,
          severity: 'success',
          message: isDeleting
            ? 'Role expiration deleted successfully!'
            : 'Role expiration added successfully!',
        });

        queryClient.setQueryData<RoleMember[]>(
          [UserKeys.RoleMembers, roleName],
          (oldData) => {
            if (!oldData) return;
            return oldData.map(
              (member) =>
                variables.find((v) => v.userEmail === member.userEmail) ||
                member
            );
          }
        );
      } else {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: Messages.Errors.Network.Updating(`expiry date`),
        });
      }
    },
    onMutate: (variables) => {
      const emails = variables.map((v) => v.userEmail);
      setUpdatingMemberEmails((prev) => [...prev, ...emails]);
    },
    onError: () => {
      setSnackbar({
        open: true,
        severity: 'error',
        message: Messages.Errors.Network.Updating(`expiry date`),
      });
    },
    onSettled: (_, __, variables) => {
      const emailsToRemove = variables ? variables.map((v) => v.userEmail) : [];
      setUpdatingMemberEmails((prev) =>
        prev.filter((email) => !emailsToRemove.includes(email))
      );
    },
  });

  const columns: GridColDef<RoleMember>[] = useMemo(
    () => [
      {
        field: 'userEmail',
        headerName: 'Email',
        flex: 2,
        display: 'flex',
        renderCell: (params) => {
          const avatar = (
            <Stack flexDirection="row" alignItems="center" gap={1}>
              {params.row.memberType === 'GROUP' ? (
                <Tooltip title="Group">
                  <Group
                    fontSize="small"
                    color="action"
                    data-testid={`group-icon-${params.row.userEmail}`}
                  />
                </Tooltip>
              ) : null}
              <Typography variant="body2">{params.row.userEmail}</Typography>
            </Stack>
          );
          return avatar;
        },
      },
      {
        field: 'expiryTime',
        headerName: 'Expiration',
        flex: 1,
        renderCell: (params) => {
          const hasAddMemberRole = isAbleToSeeUIElement(
            administrationRoles,
            AdministrationRoles.AddMemberToRole.value
          );

          if (
            currentRole?.roleName === 'Admin' &&
            params.row.userEmail.toLowerCase() === adminEmail.toLowerCase()
          ) {
            return (
              <Tooltip title="The gPanel admin cannot have an expiry date">
                <span>
                  <RoleMembershipExpirationChip
                    currentExpirationTime={params.row.expiryTime}
                    disabled={true}
                    onExpirationChange={() => {}}
                  />
                </span>
              </Tooltip>
            );
          }

          return (
            <RoleMembershipExpirationChip
              currentExpirationTime={params.row.expiryTime}
              disabled={!hasAddMemberRole}
              isUpdating={updatingMemberEmails.includes(params.row.userEmail)}
              onExpirationChange={(newExpirationTime) => {
                const updatedMember = {
                  ...params.row,
                  expiryTime: newExpirationTime || undefined,
                };
                updateMemberMutate([updatedMember]);
              }}
            />
          );
        },
      },
    ],
    [
      administrationRoles,
      adminEmail,
      currentRole?.roleName,
      updatingMemberEmails,
      updateMemberMutate,
    ]
  );

  if (isLoading) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', minHeight: 300 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  function CustomToolbar() {
    return (
      <SharedToolbar noColumn noFilter>
        <Box>
          <Tooltip
            arrow
            title={
              !isAbleToSeeUIElement(
                administrationRoles,
                AdministrationRoles.AddMemberToRole.value
              )
                ? Messages.Errors.Permission.Acting(
                    AdministrationRoles.AddMemberToRole.name
                  )
                : ''
            }
          >
            <span>
              <Button
                variant="contained"
                onClick={() => setOpenAddMembers(true)}
                disabled={
                  !isAbleToSeeUIElement(
                    administrationRoles,
                    AdministrationRoles.AddMemberToRole.value
                  )
                }
              >
                Add Member
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            arrow
            title={
              !isAbleToSeeUIElement(
                administrationRoles,
                AdministrationRoles.RemoveMemberFromRole.value
              )
                ? Messages.Errors.Permission.Acting(
                    AdministrationRoles.RemoveMemberFromRole.name
                  )
                : 'Remove'
            }
          >
            <span>
              <IconButton
                onClick={() => setOpenDeleteMembersFromRole(true)}
                disabled={
                  selectedRows.length === 0 ||
                  !isAbleToSeeUIElement(
                    administrationRoles,
                    AdministrationRoles.RemoveMemberFromRole.value
                  )
                }
              >
                <Delete />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip arrow title="Refresh">
            <IconButton
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: [UserKeys.RoleMembers, roleName],
                });
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </SharedToolbar>
    );
  }

  const sortedMembers = data
    ? [...data.sort((a, b) => a.userEmail.localeCompare(b.userEmail))]
    : [];

  if (currentRole) {
    return (
      <Box sx={{ position: 'relative', height: '100%' }}>
        <Box sx={{ height: '100%', overflowY: 'auto' }}>
          <SharedDataGridClient
            slots={{
              toolbar: CustomToolbar,
              ...createGridOverlays('Role Members', <Group />),
            }}
            getRowId={(row) => row.userEmail}
            rows={sortedMembers}
            columns={columns}
            loading={membersLoading || isFetching}
            disableColumnSelector
            checkboxSelection
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
          />
        </Box>
        {selectedRows.length > 0 && openDeleteMembersFromRole ? (
          <DeleteRoleMembersDialog
            open={openDeleteMembersFromRole}
            handleClose={() => setOpenDeleteMembersFromRole(false)}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            memberList={sortedMembers}
            changeLogRoles={changeLogRoles}
            selectedRole={currentRole}
          />
        ) : null}
        {openAddMembers ? (
          <AddRoleMemberDialog
            open={openAddMembers}
            handleClose={() => setOpenAddMembers(false)}
            selectedRole={currentRole}
            changeLogRoles={changeLogRoles}
            membersList={sortedMembers}
          />
        ) : null}
      </Box>
    );
  }
}
